/*
 * Checkout speed (29 Oct 2026). Loaded once per page from layout/theme.liquid (defer). Three jobs, each fails safe:
 *
 * 1. LOADING STATE. Every Checkout button (basket drawer, basket page, anything with name="checkout") carries an
 *    arrow in a fixed slot at its right edge. On a press the label becomes "Going to checkout" and the arrow morphs
 *    into a spinner in the same slot; the theme's top loading bar starts and a polite live region says the same. The form still submits natively (nothing is
 *    disabled, so name="checkout" is still posted). The state stays until the page unloads. If the shopper comes back
 *    with Back (bfcache), pageshow resets it. A repeat press within 15 s is ignored; after 30 s still on the page the
 *    button is given back, so a stalled navigation can be retried.
 *
 * 2. CHECKOUT WARMING, on add to basket only. Any successful add (product form, Box Set v2, drawer upsell, quick add,
 *    any script that calls /cart/add) starts a background download of Shopify's checkout static files, so the
 *    checkout page finds them in the browser cache. It works in every browser, Safari / iOS included. Shopify's own
 *    /checkouts/internal/preloads.js does the same only in Chromium (it reads navigator.connection), and on page load.
 *    - The list of files is Shopify's own, read at run time: the preloads.js URL is taken from Shopify's own script
 *      tag on this page, fetched once WITHOUT cookies (credentials: 'omit'). Its scripts / styles / font / image
 *      arrays are read; if Shopify changes the format, every URL in the text is taken instead (v2, 29 Oct). No file
 *      name is written here. Nothing found: one `checkout_warm:none` event (detail 404 / empty / no_urls).
 *    - Only static files are fetched: every URL must be https on this store's /cdn/ path or on cdn.shopify.com /
 *      extensions.shopifycdn.com / fonts.shopifycdn.com, and must end in .js .css .woff2 .woff .png .svg .jpg .webp.
 *      A path starting /checkout, /checkouts, /cart, /account, /apps, /wallets or /shopify_pay is refused whatever
 *      the host. Each file is fetched the way the checkout page itself will ask for it (same-origin files with the
 *      browser's normal same-origin credentials, cross-origin fonts anonymously, the logo like an <img>), because
 *      Chrome keeps credentialed and anonymous copies apart in its cache and a mismatch would not be reused.
 *    - Low priority, three at a time. Stops at once when Checkout is pressed (so the checkout gets the whole link) or
 *      the page is left; resumes on the next page if it had not finished (sessionStorage), never starts on page view.
 *    - Skipped when the browser asks to save data or is on 2G (the same rule as Shopify's preloads.js).
 *    - If preloads.js is missing, unreadable, or any step throws, it warms nothing and never breaks the page.
 *
 * 3. DRAWER EXPRESS BUTTONS, WALLETS FIRST (v3, owner 29 Oct). With the drawer setting on, an add starts Shopify's
 *    wallet module and fetches the Shop Pay / Apple Pay / Google Pay buttons (sections/os-drawer-wallets.liquid) at
 *    once; the checkout warm-up waits for them (ready + 1.5 s, cap 6 s). An earlier basket: loaded when the drawer
 *    opens. A plain page view loads no wallet code. The gift message and attributes are saved before a wallet tap.
 *
 * 4. DRAWER CHECKOUT AS A GET (block 4 below), Apple WebKit only: when the basket already holds the drawer's note and
 *    attributes it goes straight to GET /checkout (Safari can then use the warmed files); otherwise the normal POST.
 *    Nothing writes to the basket at the press, so nothing in the drawer redraws (v3.2).
 *
 * Rollback: remove the one script tag in layout/theme.liquid (the drawer then behaves exactly as before, apart from
 * the express buttons block in sections/cart-drawer.liquid, which has its own rollback note).
 */
(function () {
  'use strict';
  if (window.__osCheckoutSpeed) return;
  window.__osCheckoutSpeed = { v: '2026-10-29' };
  var OS = window.__osCheckoutSpeed;

  function ss(get, key, val) {
    try { return get ? sessionStorage.getItem(key) : sessionStorage.setItem(key, val); } catch (e) { return null; }
  }

  /* ---------------------------------------------------------------- 1. loading state */
  /* v3.1 (owner, 29 Oct): every Checkout button carries an arrow in a fixed icon slot near its right edge (inside the
     button's own right padding, absolutely placed, so the button's box never changes). On a press the label becomes
     "Going to checkout" (an overlay in the button's own type, uppercase and letter-spacing) and the arrow morphs into a
     spinner in the same slot (~180 ms), then spins. Reduced motion: instant swap to a static progress glyph. Back
     (bfcache) returns to the idle arrow. Screen readers: aria-busy, aria-label and a polite live region. */
  var LABEL = 'Going to checkout';
  // Two glyphs in one fixed 14 px slot, each on its own compositor layer: the arrow, and a 3/4 arc in the SAME stroke
  // width and colour. The change is transform + opacity only (no layout, no SVG attribute animation), so it runs on the
  // compositor and keeps going while the browser is busy navigating. Ease-out cubic-bezier(.2,0,0,1); the arrow leaves
  // completely (0-90 ms) before the arc arrives (90-170 ms), then the arc spins at 0.95 s a turn.
  var SVG = '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" focusable="false">';
  var ICON = '<span class="os-ck-a">' + SVG + '<path d="M2.5 8h10.5M9 4l4 4-4 4"/></svg></span>' +
    '<span class="os-ck-s"><span class="os-ck-r">' + SVG + '<path d="M8 2a6 6 0 1 1-6 6"/></svg></span></span>';
  var EASE = 'cubic-bezier(.2,0,0,1)';
  var CSS = '' +
    'button[data-os-ck]{position:relative}' +
    '.os-ck-icon{position:absolute;inset-inline-end:max(10px,calc((1.75rem - 14px) / 2));top:calc(50% - 7px);width:14px;height:14px;' +
      'pointer-events:none;color:rgb(var(--button-text-color))}' +
    '.os-ck-a,.os-ck-s,.os-ck-r{position:absolute;inset:0;display:block;will-change:transform,opacity;backface-visibility:hidden;transform:translateZ(0)}' +
    '.os-ck-a svg,.os-ck-r svg{display:block}' +
    '.os-ck-s{opacity:0}' +
    // Sequential hand-off, never both shapes in one frame: the arrow slides 3 px right and is fully gone at 90 ms; the
    // arc starts at 90 ms (scale .85 -> 1, fade in, done at 170 ms), then spins.
    '.os-ck-busy .os-ck-a{animation:os-ck-out 90ms ' + EASE + ' both}' +
    // one animation per element, so each can run on the compositor: the fade/turn-in on the slot, the spin inside it
    '.os-ck-busy .os-ck-s{animation:os-ck-in 80ms ' + EASE + ' 90ms both}' +
    '.os-ck-busy .os-ck-r{animation:os-ck-spin .95s linear 170ms infinite}' +
    '@keyframes os-ck-out{from{opacity:1;transform:translateZ(0) translateX(0)}to{opacity:0;transform:translateZ(0) translateX(3px)}}' +
    '@keyframes os-ck-in{from{opacity:0;transform:translateZ(0) scale(.85)}to{opacity:1;transform:translateZ(0) scale(1)}}' +
    // optical centring on the label's capitals: up one device pixel on 2x and 3x screens (whole device pixels stay crisp)
    '@media (min-resolution:2dppx){.os-ck-icon{top:calc(50% - 7.5px)}}' +
    '@media (min-resolution:3dppx){.os-ck-icon{top:calc(50% - 7px - 1px / 3)}}' +
    '@keyframes os-ck-spin{from{transform:translateZ(0) rotate(0)}to{transform:translateZ(0) rotate(360deg)}}' +
    '@media (prefers-reduced-motion:reduce){.os-ck-busy .os-ck-a{animation:none;opacity:0}' +
      '.os-ck-busy .os-ck-s,.os-ck-busy .os-ck-r{animation:none;opacity:1;transform:none}}' +
    // the theme fades button text colour over .45 s; the swap must be in one frame, so no transition while busy
    '.os-ck-busy{color:transparent!important;cursor:progress;transition:none!important}' +
    '.os-ck-busy>*:not(.os-ck-loading):not(.os-ck-icon){visibility:hidden}' +
    '.os-ck-loading{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;' +
      'padding-inline:calc(max(10px,calc((1.75rem - 14px) / 2)) + 20px);' +
      'color:rgb(var(--button-text-color));white-space:nowrap;pointer-events:none;overflow:hidden}' +
    '.os-ck-sr{position:absolute!important;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);border:0;white-space:nowrap}';

  var styled = false, live = null, busy = [], pressedAt = 0, giveBack = 0;

  function ensureUi() {
    if (!styled) {
      styled = true;
      var st = document.createElement('style');
      st.id = 'os-checkout-speed-css';
      st.textContent = CSS;
      document.head.appendChild(st);
    }
    if (!live || !live.isConnected) {
      live = document.createElement('div');
      live.className = 'os-ck-sr';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      document.body.appendChild(live);
    }
  }

  // Idle arrow on every Checkout button, including the ones the drawer / basket page re-render.
  function decorate() {
    try {
      var bs = document.querySelectorAll('button[name="checkout"]:not([data-os-ck])');
      if (!bs.length) return;
      ensureUi();
      Array.prototype.forEach.call(bs, function (b) {
        b.setAttribute('data-os-ck', '');
        var i = document.createElement('span');
        i.className = 'os-ck-icon';
        i.setAttribute('aria-hidden', 'true');
        i.innerHTML = ICON;
        b.appendChild(i);
      });
    } catch (e) {}
  }
  var decoQueued = false;
  function queueDecorate() { if (decoQueued) return; decoQueued = true; requestAnimationFrame(function () { decoQueued = false; decorate(); }); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', decorate); else decorate();
  document.addEventListener('cart-drawer:refreshed', queueDecorate);
  try {
    new MutationObserver(function (list) {
      for (var k = 0; k < list.length; k++) if (list[k].addedNodes.length) { queueDecorate(); return; }
    }).observe(document.documentElement, { childList: true, subtree: true });
  } catch (e) {}

  function setBusy(btn) {
    if (!btn || btn.classList.contains('os-ck-busy')) return;
    if (btn.localName === 'button' && !btn.hasAttribute('data-os-ck')) decorate();
    var span = document.createElement('span');
    span.className = 'os-ck-loading';
    span.setAttribute('aria-hidden', 'true');
    span.textContent = LABEL;
    btn.appendChild(span);
    btn.classList.add('os-ck-busy');
    btn.setAttribute('aria-busy', 'true');
    btn.setAttribute('aria-disabled', 'true');
    btn.setAttribute('data-os-ck-label', btn.getAttribute('aria-label') || '');
    btn.setAttribute('aria-label', LABEL);
    busy.push(btn);
  }

  function reset() {
    pressedAt = 0;
    clearTimeout(giveBack);
    busy.forEach(function (btn) {
      try {
        btn.classList.remove('os-ck-busy');
        btn.removeAttribute('aria-busy');
        btn.removeAttribute('aria-disabled');
        var l = btn.getAttribute('data-os-ck-label');
        if (l) btn.setAttribute('aria-label', l); else btn.removeAttribute('aria-label');
        btn.removeAttribute('data-os-ck-label');
        var s = btn.querySelector(':scope > .os-ck-loading');
        if (s) s.remove();
      } catch (e) {}
    });
    busy = [];
    if (live) live.textContent = '';
  }

  function showGoing(btn) {
    try {
      ensureUi();
      setBusy(btn);
      live.textContent = LABEL;
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
      clearTimeout(giveBack);
      giveBack = setTimeout(function () { if (document.visibilityState === 'visible') reset(); }, 30000);
    } catch (e) {}
  }

  // ev.submitter is Safari 15.4+; older browsers fall back to the Checkout button pressed in the last second.
  var lastCk = null;
  document.addEventListener('click', function (ev) {
    try {
      var b = ev.target && ev.target.closest && ev.target.closest('button[name="checkout"], input[name="checkout"]');
      if (b) lastCk = { b: b, t: Date.now() };
    } catch (e) {}
  }, true);
  function checkoutSubmitter(ev) {
    if ('submitter' in ev) { var b = ev.submitter; return b && b.name === 'checkout' ? b : null; }
    if (lastCk && Date.now() - lastCk.t < 1000 && lastCk.b.form === ev.target) return lastCk.b;
    return null;
  }

  // Bubble phase on document: runs after every other submit handler, so a form some other code cancelled is left alone.
  document.addEventListener('submit', function (ev) {
    try {
      var btn = checkoutSubmitter(ev);
      if (!btn) return;
      if (pressedAt && Date.now() - pressedAt < 15000) { ev.preventDefault(); return; } // a second press while going
      if (ev.defaultPrevented) return;
      pressedAt = Date.now();
      stopWarm();
      // (v3.1: the drawer's wallet buttons are no longer removed on a press: in the lab it bought nothing measurable
      // and the buttons vanishing under the busy Checkout button looked like a glitch.)
      showGoing(btn);
      if (directOk(ev.target)) { ev.preventDefault(); direct(ev.target); }
    } catch (e) {}
  });

  /* ---------------------------------------------------------------- 4. drawer Checkout as a GET to /checkout */
  // Native: POST /cart (applies note + attributes) -> 302 shop.app -> 302 /checkouts/cn/<token>. Safari/WebKit loads a
  // page reached through a form POST with its sub-resources re-checked against the server, so a checkout reached that
  // way re-downloads the files the browser already holds (measured in the lab, see CHECKOUT_SPEED_BUILD.md). Here the
  // drawer's own fields are first saved with /cart/update.js (exactly what the POST would have applied: the gift message
  // and the two theme attributes), then the browser GETs /checkout. Any failure or a 5 s wait falls back to the native
  // POST, so the shopper always gets to checkout. Basket page: unchanged (native POST; it also posts updates[] and
  // discount). Switch off: set DIRECT to false.
  // Apple WebKit only (Safari and every iOS browser report navigator.vendor "Apple Computer, Inc."): that is where the
  // POST loses the cache. Chrome / Firefox already reuse the cache after the POST, and there the save costs ~0.3-0.5 s.
  // window.__osCkNoDirect is a lab switch (the test browser sets it to compare with the POST).
  var DIRECT = !window.__osCkNoDirect && /^Apple/.test(navigator.vendor || '');
  function directOk(form) {
    if (!DIRECT || !form || !window.fetch || !form.closest || !form.closest('cart-drawer')) return false;
    if ((form.getAttribute('method') || '').toLowerCase() !== 'post') return false;
    var a;
    try { a = new URL(form.getAttribute('action') || '', location.href); } catch (e) { return false; }
    if (a.origin !== location.origin || !/\/cart\/?$/.test(a.pathname)) return false;
    // Only fields we know how to carry: note, attributes[...], checkout. Anything else (e.g. updates[]) -> native.
    for (var i = 0; i < form.elements.length; i++) {
      var n = form.elements[i].name;
      if (n && n !== 'note' && n !== 'checkout' && !/^attributes\[[^\]]+\]$/.test(n)) return false;
    }
    return true;
  }
  /* v3.2: NO cart write at the press. Any request to /cart/update|change|add|clear makes Shopify's own wallet component
     (portable-wallets: a PerformanceObserver on those URLs) re-query the basket and redraw its buttons, so a save here
     made the drawer's wallet buttons blink out under the busy Checkout (owner, iPhone, 29 Oct). Instead: if the basket
     on the server already holds exactly what the form would post (note + attributes, read with a GET /cart.js when the
     drawer opened or re-rendered; a GET is not watched), go straight to GET /checkout; otherwise use the theme's normal
     POST /cart, which carries the fields itself and is a navigation, not a watched request. */
  function direct(form) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    var f = drawerFields(form), c = OS.cartState, same = !!c;
    if (same && (f.note || '') !== (c.note || '')) same = false;
    if (same) Object.keys(f.attributes).forEach(function (k) { if ((f.attributes[k] || '') !== ((c.attributes || {})[k] || '')) same = false; });
    OS.pressPath = same ? 'get' : 'post';
    if (same) { location.assign(root + 'checkout'); return; }
    try {
      var h = document.createElement('input');
      h.type = 'hidden'; h.name = 'checkout'; h.value = '';
      form.appendChild(h);
      HTMLFormElement.prototype.submit.call(form); // no second submit event
    } catch (e) { location.assign(root + 'checkout'); }
  }
  OS.directOk = directOk;

  // Links to /checkout (none in the theme today; covers any added later). Plain left click, same tab only.
  document.addEventListener('click', function (ev) {
    try {
      if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
      var a = ev.target && ev.target.closest && ev.target.closest('a[href]');
      if (!a || (a.target && a.target !== '_self')) return;
      var u = new URL(a.href, location.href);
      if (u.origin !== location.origin || !/^\/checkouts?(\/|$)/.test(u.pathname)) return;
      pressedAt = Date.now();
      stopWarm();
      showGoing(a);
    } catch (e) {}
  });

  // Back / forward cache: the page comes back exactly as it was left, so put the buttons back (and carry on
  // warming if an add had started it and it had not finished).
  addEventListener('pageshow', function (ev) {
    if (!ev.persisted) return;
    reset();
    try { if (warming && !ctrl) { warming = false; if (ss(true, WARM_KEY) === '1') warm('bfcache'); } } catch (e) {}
  });

  /* ---------------------------------------------------------------- 2. checkout warming */
  var WARM_KEY = 'os_ck_warm'; // '1' = started on an add, not finished; '2' = finished this session
  var ctrl = null, warming = false, inflight = [];
  var ASSET_HOSTS = { 'cdn.shopify.com': 1, 'extensions.shopifycdn.com': 1, 'fonts.shopifycdn.com': 1 };
  var ASSET_EXT = /\.(js|css|woff2?|png|svg|jpe?g|webp)$/i;
  var NEVER = /^\/(checkouts?|cart|account|apps|wallets|shopify_pay|payments)(\/|$|\.)/i;

  // The one gate every warming URL passes. Static files only.
  function staticAsset(raw) {
    if (typeof raw !== 'string' || raw.length > 600) return null;
    var u;
    try { u = new URL(raw, location.origin); } catch (e) { return null; }
    if (u.protocol !== 'https:' || u.username || u.password) return null;
    if (NEVER.test(u.pathname) || !ASSET_EXT.test(u.pathname)) return null;
    if (u.host === location.host) { if (u.pathname.indexOf('/cdn/') !== 0) return null; }
    else if (!ASSET_HOSTS[u.host]) return null;
    return u.href;
  }
  OS.staticAsset = staticAsset;

  function preloadsSrc() {
    var s = document.querySelector('script[src*="/checkouts/internal/preloads.js"]');
    if (!s) return null;
    var u;
    try { u = new URL(s.getAttribute('src'), location.href); } catch (e) { return null; }
    // Shopify's own first-party tag only (not shop.app's), exactly this path.
    if (u.origin !== location.origin || u.pathname !== '/checkouts/internal/preloads.js') return null;
    return u.href;
  }

  var IMG_EXT = /\.(png|svg|jpe?g|webp)$/i;
  function add(out, seen, raw) {
    var h = staticAsset(raw);
    if (h && !seen[h]) { seen[h] = 1; out.push({ url: h, img: IMG_EXT.test(new URL(h).pathname) }); }
  }
  // First try: Shopify's arrays as written today: `var scripts = [...]`, `var styles = [...]`, fonts, images.
  function parseNamed(text) {
    var out = [], seen = {};
    ['scripts', 'styles', 'fontPrefetchUrls', 'imgPrefetchUrls'].forEach(function (name) {
      var m = new RegExp('var\\s+' + name + '\\s*=\\s*(\\[[^\\]]*\\])').exec(text);
      if (!m) return;
      var arr;
      try { arr = JSON.parse(m[1]); } catch (e) { return; }
      if (!Array.isArray(arr)) return;
      arr.forEach(function (x) { add(out, seen, x); });
    });
    return out;
  }
  // Fallback, whatever the format: every URL-looking string in the text (absolute https://…, protocol-relative
  // //host/…, root-relative /cdn/…), JSON-escaped slashes undone, de-duplicated, each through the same staticAsset()
  // gate. A renamed variable, an object map, minified JS or JSON all still give the same files.
  var URL_RE = /(?:https:\/\/|\/\/)[a-z0-9.-]+\.[a-z]{2,}\/[^\s"'`<>()\\,;{}\[\]]+|\/cdn\/[^\s"'`<>()\\,;{}\[\]]+/gi;
  function parseAny(text) {
    var out = [], seen = {}, m;
    var t = text.replace(/\\\//g, '/');
    URL_RE.lastIndex = 0;
    while ((m = URL_RE.exec(t)) && out.length < 500) {
      var before = m.index ? t.charAt(m.index - 1) : '';
      if (m[0].charAt(1) === '/') {
        if (before === ':') continue; // the tail of http:// or another scheme: never upgraded, never taken
        add(out, seen, 'https:' + m[0]);
      } else if (m[0].indexOf('/cdn/') === 0) {
        if (before && !/["'`(=,\[\s]/.test(before)) continue; // /cdn/ in the middle of some other URL or word
        add(out, seen, m[0]);
      } else add(out, seen, m[0]);
    }
    return out;
  }
  function parseList(text) {
    var a = parseNamed(text);
    if (a.length) { OS.listFormat = 'named'; return a.slice(0, 500); }
    OS.listFormat = 'generic';
    return parseAny(text).slice(0, 500);
  }
  OS.parseList = parseList;

  // Nothing to warm (404, empty, no usable URLs): one event through the theme tracker, at most once per page view.
  var noneSent = false;
  function reportNone(reason) {
    if (noneSent) return;
    noneSent = true;
    try { if (typeof window.__OS_TRACK__ === 'function') window.__OS_TRACK__('checkout_warm:none', { 'ep.os_detail': String(reason).slice(0, 40) }); } catch (e) {}
  }

  function slowOrSaving() {
    try {
      var c = navigator.connection;
      if (!c) return false; // Safari / Firefox: no signal, so warm
      if (c.saveData) return true;
      return /(^|-)2g$/.test(c.effectiveType || '');
    } catch (e) { return false; }
  }

  // Cancels only what is still downloading. A finished file's request is never aborted afterwards (WebKit can drop
  // the cache entry of a request whose signal is aborted after it completed).
  function stopWarm() {
    try { if (ctrl) ctrl.abort(); } catch (e) {}
    inflight.forEach(function (c) { try { c.abort(); } catch (e) {} });
    inflight = [];
    ctrl = null;
  }
  OS.stopWarm = stopWarm;

  function warm(reason) {
    if (pressedAt || warming || ss(true, WARM_KEY) === '2' || slowOrSaving() || !window.fetch || !window.AbortController) return; // never after a Checkout press
    var src = preloadsSrc();
    if (!src) return;
    warming = true;
    ss(false, WARM_KEY, '1');
    ctrl = new AbortController();
    var signal = ctrl.signal;
    var t0 = performance.now();
    OS.warm = { reason: reason, started: Math.round(t0), done: 0, failed: 0, total: 0 };
    var why = '';
    fetch(src, { credentials: 'omit', cache: 'no-store', signal: signal, priority: 'low' })
      .then(function (r) { if (!r.ok) { why = String(r.status); return ''; } return r.text(); })
      .then(function (text) {
        text = text || '';
        var list = parseList(text);
        OS.warm.total = list.length;
        OS.warm.format = OS.listFormat;
        if (!list.length) { // nothing usable: do nothing, say so once, and do not retry this visit
          warming = false; ss(false, WARM_KEY, '2');
          reportNone(why || (/\S/.test(text) ? 'no_urls' : 'empty'));
          OS.warm.none = why || (/\S/.test(text) ? 'no_urls' : 'empty');
          return;
        }
        var i = 0, active = 0;
        function next() {
          if (signal.aborted) return;
          if (i >= list.length) {
            if (active === 0) { ss(false, WARM_KEY, '2'); OS.warm.ms = Math.round(performance.now() - t0); warming = false; }
            return;
          }
          var item = list[i++];
          active++;
          var same = item.url.indexOf(location.origin + '/') === 0;
          var init = same ? { credentials: 'same-origin', mode: 'cors' }
            : item.img ? { credentials: 'include', mode: 'no-cors' } : { credentials: 'omit', mode: 'cors' };
          var one = new AbortController();
          inflight.push(one);
          init.signal = one.signal; init.priority = 'low';
          fetch(item.url, init)
            .then(function (r) { return r.arrayBuffer(); }) // read the body so the cache entry is complete
            .then(function () { OS.warm.done++; }, function () { OS.warm.failed++; })
            .then(function () { inflight.splice(inflight.indexOf(one), 1); active--; next(); });
        }
        next(); next(); next();
      })
      .catch(function (e) { warming = false; if (!(e && e.name === 'AbortError')) reportNone('network'); });
  }
  OS.warmNow = warm;

  /* ---------------------------------------------------------------- add-to-basket detection */
  /* ---------------------------------------------------------------- 3. drawer express buttons */
  // The drawer prints an empty [data-os-drawer-wallets] box. Its content (Shopify's express buttons) is fetched from
  // sections/os-drawer-wallets.liquid on an add or when the drawer opens, and again after each drawer re-render (the
  // theme replaces the drawer's children on every basket change). Any failure leaves the box empty: Checkout is above.
  var walletHtml = null, walletBusy = null, walletsWanted = false;

  /* v3.1: did the drawer buttons render on this device? One event per page view through the theme's own sender:
     drawer_wallets:ready  detail "<theme id>|<wallet tags>|<ms since the add or drawer open>"
     drawer_wallets:none   detail "<theme id>|<reason>": section_<http status> / section_network / section_empty (Shopify
     printed no buttons) / no_box (the drawer has no box: setting off or another theme's drawer) / not_defined (Shopify's
     wallet module never loaded) / skeleton (still loading after 12 s) / no_buttons (rendered, no wallet offered). */
  var walletSent = false, walletT0 = 0, walletWatch = 0;
  function themeId() { try { return String(Shopify.theme.id); } catch (e) { return '?'; } }
  function walletEvent(name, detail) {
    if (walletSent) return;
    walletSent = true;
    clearTimeout(walletWatch);
    var d = detail.charAt(0) === '{' ? detail.replace(/^\{/, '{"theme":"' + themeId() + '",') : JSON.stringify({ theme: themeId(), reason: detail });
    try { if (typeof window.__OS_TRACK__ === 'function') window.__OS_TRACK__(name, { 'ep.os_detail': d.slice(0, 160) }); } catch (e) {}
    OS.walletEvent = name + ' ' + d;
  }
  function walletNone(reason) { walletEvent('drawer_wallets:none', reason); }

  /* v3.1, owner's rule for the drawer (product page and basket page unchanged):
     Apple Pay available in this browser -> Shop Pay + Apple Pay; otherwise -> Shop Pay + Google Pay. PayPal and any
     other extra never show in the drawer. Decided BEFORE Shopify renders, by editing the list Shopify itself reads
     (the wallet-configs attribute on <shopify-accelerated-checkout-cart>), using the same availability test Shopify
     runs (ApplePaySession.canMakePayments()). After rendering it is checked: if Apple Pay was kept but its button
     never appeared, the buttons are rebuilt with Google Pay instead. Anything unrecognised: left exactly as Shopify sent
     it (fail open). */
  var WALLET_TAG = { 'shop-pay-wallet-button': 'shop_pay', 'shopify-apple-pay-button': 'apple_pay', 'shopify-google-pay-button': 'google_pay', 'shopify-paypal-button': 'paypal' };
  var appleFailed = false;
  function appleAvailable() {
    if (appleFailed) return false;
    try { return !!(window.ApplePaySession && ApplePaySession.canMakePayments && ApplePaySession.canMakePayments()); } catch (e) { return false; }
  }
  function pickWallets(node) {
    try {
      var host = node.matches && node.matches('shopify-accelerated-checkout-cart') ? node : node.querySelector && node.querySelector('shopify-accelerated-checkout-cart');
      if (!host) return;
      var cfg = JSON.parse(host.getAttribute('wallet-configs') || 'null');
      if (!Array.isArray(cfg) || !cfg.length || !cfg.every(function (c) { return c && typeof c.name === 'string'; })) return;
      var names = cfg.map(function (c) { return c.name; });
      if (names.indexOf('shop_pay') < 0 && names.indexOf('apple_pay') < 0 && names.indexOf('google_pay') < 0) return;
      var keep = appleAvailable() && names.indexOf('apple_pay') >= 0 ? ['shop_pay', 'apple_pay'] : ['shop_pay', 'google_pay'];
      var out = cfg.filter(function (c) { return keep.indexOf(c.name) >= 0; });
      if (!out.length) return; // fail open
      host.setAttribute('wallet-configs', JSON.stringify(out));
      OS.walletPlan = out.map(function (c) { return c.name; }).join(',');
    } catch (e) {} // fail open
  }
  // Wallets actually on screen in the drawer, by Shopify's own element names.
  function walletsShown() {
    var el = document.querySelector('cart-drawer [data-os-drawer-wallets] shopify-accelerated-checkout-cart');
    if (!el) return [];
    var out = [];
    Array.prototype.forEach.call(el.querySelectorAll('*'), function (n) { var w = WALLET_TAG[n.localName]; if (w && out.indexOf(w) < 0 && n.getBoundingClientRect().height > 0) out.push(w); });
    return out;
  }
  function walletTags() {
    var el = document.querySelector('cart-drawer [data-os-drawer-wallets] shopify-accelerated-checkout-cart');
    if (!el) return [];
    var root = el.shadowRoot || el, out = [];
    Array.prototype.forEach.call(root.querySelectorAll('*'), function (n) { var t = n.localName; if (/pay|wallet/.test(t) && out.indexOf(t) < 0) out.push(t); });
    return out;
  }
  function watchWallets() {
    if (walletSent || walletWatch) return;
    var start = Date.now(), last = start;
    (function poll() {
      walletWatch = 0;
      if (walletSent) return;
      try {
        // v3.3 (30 Oct): the 12 s clock runs only while the drawer is open on a visible page. Closed, the buttons have
        // no height and never count as shown; and a page frozen in the back/forward cache must not come back already
        // past 12 s (both gave false `skeleton` reports on an iPhone, 30 Oct 06:02 / 06:16 UTC).
        var now = Date.now(), paused = !document.querySelector('cart-drawer[open]') || document.visibilityState !== 'visible';
        if (paused || now - last > 2000) start += now - last;
        last = now;
        if (paused) { walletWatch = setTimeout(poll, 1000); return; }
        var el = document.querySelector('cart-drawer [data-os-drawer-wallets] shopify-accelerated-checkout-cart');
        if (el && walletsReady()) {
          var shown = walletsShown();
          // Apple Pay was planned but did not appear 3 s after the others: rebuild with Google Pay instead.
          if (/apple_pay/.test(OS.walletPlan || '') && shown.indexOf('apple_pay') < 0 && shown.length && Date.now() - start > 3000 && !appleFailed) {
            appleFailed = true;
            var box = document.querySelector('cart-drawer [data-os-drawer-wallets]');
            if (box) { box.replaceChildren(); walletWatch = 0; fillWallets(); return; }
          }
          if (shown.length && (!/apple_pay/.test(OS.walletPlan || '') || shown.indexOf('apple_pay') >= 0 || appleFailed)) {
            return walletEvent('drawer_wallets:ready', JSON.stringify({ shown: shown.join(','), ms: Date.now() - (walletT0 || start) }));
          }
        }
        if (Date.now() - start > 12000) {
          if (!document.querySelector('cart-drawer [data-os-drawer-wallets]')) return walletNone('no_box');
          if (!el) return walletNone('section_empty');
          if (!window.customElements || !customElements.get('shopify-accelerated-checkout-cart')) return walletNone('not_defined');
          return walletNone(walletTags().length ? 'skeleton' : 'no_buttons');
        }
      } catch (e) { return; }
      walletWatch = setTimeout(poll, 300);
    })();
  }
  function fillWallets() {
    try {
      var box = document.querySelector('cart-drawer [data-os-drawer-wallets]');
      if (!box || box.childElementCount) return;
      if (walletHtml) {
        var doc = new DOMParser().parseFromString(walletHtml, 'text/html');
        var src = doc.querySelector('[data-os-wallets-src]');
        if (!src || !src.children.length) { walletNone('section_empty'); return; }
        var nodes = Array.prototype.map.call(src.children, function (n) { return document.importNode(n, true); });
        nodes.forEach(pickWallets);
        box.replaceChildren.apply(box, nodes);
        if (window.Shopify && Shopify.PaymentButton && Shopify.PaymentButton.init) Shopify.PaymentButton.init();
        watchWallets();
        return;
      }
      loadWallets();
    } catch (e) {}
  }
  var walletsUsed = false; // v3.3: wallets were asked for on this page view (an add or the drawer opening)
  function loadWallets(fresh) {
    walletsWanted = true;
    if (walletBusy || !walletsOn()) return;
    walletsUsed = true;
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    walletBusy = fetch(root + '?section_id=os-drawer-wallets', { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) walletNone('section_' + r.status); return r.ok ? r.text() : null; })
      .then(function (t) { walletBusy = null; if (t) { walletHtml = t; fillWallets(); } }, function () { walletBusy = null; walletNone('section_network'); });
  }
  // On a Checkout press the drawer's express buttons are taken out, which cancels whatever their frames are still
  // downloading (Google Pay is ~0.6 MB), so the checkout gets the whole connection. Back restores them (re-render).
  function dropWallets() {
    try {
      walletsWanted = false;
      document.querySelectorAll('cart-drawer [data-os-drawer-wallets]').forEach(function (b) { b.style.minHeight = b.offsetHeight + 'px'; b.replaceChildren(); });
    } catch (e) {}
  }
  // Back to a page whose wallets were in use (e.g. after Checkout): fetch them again. A page where the drawer was never
  // opened loads no wallet code on Back either (v3.3; before, every Back with a basket loaded them into the shut drawer).
  addEventListener('pageshow', function (ev) { if (ev.persisted && walletsUsed) { walletHtml = null; walletsWanted = true; document.querySelectorAll('cart-drawer [data-os-drawer-wallets]').forEach(function (b) { b.style.minHeight = ''; }); fillWallets(); } });
  document.addEventListener('dialog:before-show', function (ev) {
    try { if (ev.target && ev.target.localName === 'cart-drawer' && walletsOn() && (addSeen || document.querySelector('cart-drawer [data-os-drawer-wallets]'))) { walletT0 = walletT0 || Date.now(); if (walletHtml) fillWallets(); else loadWallets(); } } catch (e) {}
  }, true);
  // After a drawer re-render (basket changed): fetch again, so the buttons match the new basket.
  document.addEventListener('cart-drawer:refreshed', function () {
    if (!walletsWanted) return;
    walletHtml = null;
    loadWallets();
  });

  // The theme setting "Express buttons under Checkout" puts data-os-wallets-on on <cart-drawer> (present on every page,
  // with or without a basket).
  function walletsOn() { return !!document.querySelector('cart-drawer[data-os-wallets-on]'); }
  function walletsReady() {
    var el = document.querySelector('cart-drawer [data-os-drawer-wallets] shopify-accelerated-checkout-cart');
    if (!el || !window.customElements || !customElements.get('shopify-accelerated-checkout-cart')) return false;
    var root = el.shadowRoot || el;
    return !/skeleton/i.test(root.innerHTML) && !!root.querySelector('*');
  }

  /* Wallets first (owner, 29 Oct: 79% of paid orders use a wallet). On an add: the wallet module and the drawer
     buttons start at once; the checkout warm-up waits until the buttons are ready plus 1.5 s for their frames to
     settle, or 6 s at most, so the two never share the link. Wallets off: the warm-up starts as before. */
  var READY_SETTLE = 1500, WALLET_CAP = 6000;
  function afterWallets(fn) {
    var t0 = Date.now(), seenReady = 0;
    (function poll() {
      try {
        if (!seenReady && walletsReady()) { seenReady = Date.now(); OS.walletsReadyMs = seenReady - t0; }
        if ((seenReady && Date.now() - seenReady >= READY_SETTLE) || Date.now() - t0 >= WALLET_CAP) return fn();
      } catch (e) { return fn(); }
      setTimeout(poll, 150);
    })();
  }

  var ADD_RE = /\/cart\/add(\.js|\.json)?(\?|$)/;
  var addSeen = false;
  function onAdded(reason) {
    var go = function () { try { warm(reason); } catch (e) {} };
    if (walletsOn()) {
      if (addSeen) return; // one wallets-first sequence per page view; later adds re-render via cart-drawer:refreshed
      addSeen = true;
      OS.addAt = Date.now();
      walletT0 = walletT0 || Date.now();
      setTimeout(function () { if (!walletSent && !document.querySelector('cart-drawer [data-os-drawer-wallets]') && document.querySelector('cart-drawer[open]')) walletNone('no_box'); else watchWallets(); }, 4000);
      try { if (window.Shopify && Shopify.PaymentButton && Shopify.PaymentButton.init) Shopify.PaymentButton.init(); } catch (e) {}
      walletHtml = null;
      loadWallets();
      afterWallets(go);
      return;
    }
    // Let the drawer render first; then start in the background.
    if (window.requestIdleCallback) requestIdleCallback(go, { timeout: 1500 }); else setTimeout(go, 600);
  }

  /* Gift message (the cart note) and the drawer's attributes are saved to the basket BEFORE a wallet tap. The Checkout
     button posts them itself; a wallet button takes the basket as it is on the server. So: a save 400 ms after typing
     stops, and an immediate one when a finger or key goes down on a wallet button (blurring the box is too late). */
  function drawerForm() { return document.querySelector('cart-drawer form[action$="/cart"], cart-drawer form.cart-drawer__footer'); }
  function drawerFields(form) {
    var body = { attributes: {} };
    for (var i = 0; i < form.elements.length; i++) {
      var el = form.elements[i], m = el.name && /^attributes\[([^\]]+)\]$/.exec(el.name);
      if (el.name === 'note') body.note = el.value;
      else if (m) body.attributes[m[1]] = el.value;
    }
    return body;
  }
  var savedJson = null, saveTimer = 0;
  function saveDrawer(force) {
    try {
      var form = drawerForm();
      if (!form || !window.fetch) return;
      var body = drawerFields(form), json = JSON.stringify(body);
      if (!force && json === savedJson) return;
      if (savedJson === null && !force) { savedJson = json; return; }
      savedJson = json;
      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      OS.lastSave = fetch(root + 'cart/update.js', { method: 'POST', credentials: 'same-origin', keepalive: true, headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: json }).catch(function () { savedJson = null; });
    } catch (e) {}
  }
  // What the basket holds now (note + attributes), read without a watched request.
  function readCart() {
    try {
      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      fetch(root + 'cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (c) { if (c) OS.cartState = { note: c.note || '', attributes: c.attributes || {} }; }, function () {});
    } catch (e) {}
  }
  document.addEventListener('dialog:before-show', function (ev) { if (ev.target && ev.target.localName === 'cart-drawer') readCart(); }, true);
  document.addEventListener('cart-drawer:refreshed', function () { if (document.querySelector('cart-drawer[open]')) readCart(); });
  if (/\/cart\/?$/.test(location.pathname)) readCart();

  /* A finger or click going down on a Checkout button blurs the gift-message box, and the theme then saves the note
     with /cart/update.js, which would make the wallet buttons redraw under the busy button. For a Checkout press that
     save is not needed (the POST carries the note; the GET is only used when the basket already has it), so the
     theme's change handler is skipped for the next 1.5 s. A wallet tap is unaffected (it still saves first). */
  var pressingUntil = 0;
  ['pointerdown', 'touchstart', 'mousedown'].forEach(function (t) {
    document.addEventListener(t, function (ev) {
      try { if (ev.target && ev.target.closest && ev.target.closest('button[name="checkout"]')) pressingUntil = Date.now() + 1500; } catch (e) {}
    }, { capture: true, passive: true });
  });
  document.addEventListener('change', function (ev) {
    try {
      if (Date.now() < pressingUntil && ev.target && ev.target.name === 'note' && ev.target.closest('cart-drawer, form[action$="/cart"]')) ev.stopImmediatePropagation();
    } catch (e) {}
  }, true);

  ['pointerdown', 'touchstart', 'keydown'].forEach(function (t) {
    document.addEventListener(t, function (ev) {
      try {
        if (t === 'keydown' && ev.key !== 'Enter' && ev.key !== ' ') return;
        if (!ev.target || !ev.target.closest || !ev.target.closest('cart-drawer [data-os-drawer-wallets]')) return;
        clearTimeout(saveTimer);
        saveDrawer(true);
      } catch (e) {}
    }, { capture: true, passive: true });
  });

  /* v3.3 (30 Oct): one event per tap on an express button, through the same sender as drawer_wallets:ready.
     drawer_wallets:tap (the drawer box) / cart_wallets:tap (the basket page), detail {"theme","wallet"}, the wallet named
     as in drawer_wallets:ready (shop_pay / apple_pay / google_pay / paypal; unknown = part of Shopify's buttons that is
     none of these). Each wallet is its own element inside <shopify-accelerated-checkout-cart>, so a tap reaches this
     page retargeted to that element and its tag names the wallet. Google Pay draws its button in a pay.google.com
     frame and that tap never reaches this page: it is caught when the frame takes the focus (window blur with the focus
     on a wallet). The focus a recorded tap left behind never counts twice. Read only: passive, never prevents
     anything, errors swallowed. */
  var tapWallet = null;
  function walletTap(path) {
    var wallet = null, host = null;
    for (var i = 0; i < path.length && !host; i++) {
      var n = path[i];
      if (!n || n.nodeType !== 1) continue;
      if (!wallet && WALLET_TAG[n.localName]) wallet = n;
      if (n.localName === 'shopify-accelerated-checkout-cart') host = n;
    }
    if (!host || !path[0] || path[0] === host) return; // the block's own padding: not a tap on a button
    var name = host.closest('cart-drawer [data-os-drawer-wallets]') ? 'drawer_wallets:tap' : /\/cart\/?$/.test(location.pathname) ? 'cart_wallets:tap' : '';
    if (!name) return;
    tapWallet = wallet || host;
    var d = JSON.stringify({ theme: themeId(), wallet: wallet ? WALLET_TAG[wallet.localName] : 'unknown' });
    try { if (typeof window.__OS_TRACK__ === 'function') window.__OS_TRACK__(name, { 'ep.os_detail': d }); } catch (e) {}
    OS.lastTap = name + ' ' + d;
  }
  document.addEventListener('click', function (ev) {
    try { walletTap(ev.composedPath ? ev.composedPath() : [ev.target]); } catch (e) {}
  }, { capture: true, passive: true });
  addEventListener('blur', function (ev) {
    try {
      if (ev.target !== window) return;
      var a = document.activeElement, path = [];
      if (!a || a === tapWallet || a === document.body) return;
      for (var n = a; n; n = n.parentNode || n.host) path.push(n);
      walletTap(path);
    } catch (e) {}
  });

  // Every fetch / XHR to /cart/add by any script on the page, once it has finished (Resource Timing; Safari 11+).
  try {
    if (window.PerformanceObserver) {
      new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (e) {
          try {
            if (!ADD_RE.test(new URL(e.name).pathname + '?')) return;
            if (e.responseStatus && (e.responseStatus < 200 || e.responseStatus >= 300)) return; // Chromium reports it
            onAdded('cart-add');
          } catch (err) {}
        });
      }).observe({ type: 'resource', buffered: false });
    }
  } catch (e) {}

  // The theme's own add events, in case Resource Timing is unavailable.
  document.addEventListener('variant:add', function () { onAdded('variant:add'); }, true);
  document.addEventListener('cart:change', function (ev) {
    try { if (ev.detail && ev.detail.baseEvent === 'variant:add') onAdded('cart:change'); } catch (e) {}
  }, true);

  // A non-JS add (native form post to /cart/add) leaves the page: mark it so the next page warms.
  document.addEventListener('submit', function (ev) {
    try {
      var f = ev.target, a = f && f.getAttribute && f.getAttribute('action');
      if (!ev.defaultPrevented && a && ADD_RE.test(new URL(a, location.href).pathname + '?')) ss(false, WARM_KEY, ss(true, WARM_KEY) || '1');
    } catch (e) {}
  });

  addEventListener('pagehide', stopWarm);

  // An add earlier in this visit that did not finish warming: carry on after this page has loaded.
  if (ss(true, WARM_KEY) === '1') {
    var resume = function () {
      var go = function () { try { warm('resume'); } catch (e) {} };
      if (window.requestIdleCallback) requestIdleCallback(go, { timeout: 3000 }); else setTimeout(go, 1500);
    };
    if (document.readyState === 'complete') resume(); else addEventListener('load', resume, { once: true });
  }
})();
